import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { batchId, clock, FG_WAREHOUSE_ID, fgSkuId, labelHead, palletId, zoneId } from './wms-fg.seed'
import { gtinOf } from './wms-outbound.seed'
import { type GabActionName, type GabStatusCode, WF_GAB } from './workflow-gab.seed'
import { type KslActionName, type KslStatusCode, WF_KSL } from './workflow-ksl.seed'

/**
 * Merge & Konsolidasi FG (C10) — GAB-01/02 (`merge_request` + `merge_request_item`), KSL-01/02 (`consolidation_task` +
 * `consolidation_task_item`). Angka mengikuti prototype v7.44: sisa Slop di CT-A / CT-B, Bal lepas di pallet, pack sisa di
 * CT-C, dan skenario konsolidasi TBC-BOLD-12 (8 + 6 + 4 karton di tiga pallet → satu pallet cukup).
 *
 * Dipasang SETELAH seed lain (`applyMergeData`) karena memakai baris `fg_label` Karton hasil `applyOutboundStock` untuk
 * tugas konsolidasi contoh. Kolom di luar ERD v010 ([ERD-GAP], laporan 14): `merge_request.{status_id, submission_id, kind,
 * jenis, sku_id, alm_no, source_text, destination_code, linked_request_id, approved_by_id, approved_at, completed_at,
 * cancel_reason}`; tabel `merge_request_item`, `consolidation_task_item`; `consolidation_task.{status_id, submission_id,
 * operator_id, jenis, package_no, freed_count, stop_reason}`; `fg_pack_stock.{label_no, origin}`.
 */

/** `YYYY-MM-DD HH:mm` WIB atau ISO UTC (bila sudah ada `T`/`Z`). */
const utc = (at: string) => (/Z$/.test(at) ? at : new Date(at.replace(' ', 'T') + ':00+07:00').toISOString())
const audit = (userId: string | null, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})
const ulil = USER_IDS.ulil

export const ALM_AREA = { 1: 'AREA-ALM-1', 2: 'AREA-ALM-2' } as const
/**
 * [kode, nama, tipe]. Area Mesin / Staging Produksi = tujuan pack (BA GAB-01 — ditetapkan Admin). Area Staging Produksi
 * dimiliki seed request (`AREA-STG-PRD-1` / `-2`, v7.50 Q-03); zona `AREA-STG-PROD` yang dulu dobel dengan itu dihapus.
 */
export const MERGE_AREAS = [
  ['AREA-ALM-1', 'Area ALM 1 (Slop)', 'ALM_AREA'],
  ['AREA-ALM-2', 'Area ALM 2 (Bal)', 'ALM_AREA'],
  ['AREA-MSN-1', 'Area Mesin Mollins 1', 'PROD_AREA']
] as const
export const mergeAreaId = (code: string) =>
  seedId(SEED_GROUP.warehouseZone, 150 + MERGE_AREAS.findIndex(([c]) => c === code))
const containerId = (code: string) => seedId(SEED_GROUP.container, ['CT-A', 'CT-B', 'CT-C'].indexOf(code) + 1)

/* ------------------------------------------------------------ barang lepas */

type Loose = {
  key: string
  unit: 'Slop' | 'Bal'
  sku: string
  batch: string
  place: string
  at: number
  retur?: boolean
}

const B20_OLD = 'TB20-20260815-0109-B001'
const B20_NEW = 'TB20-20260817-0111-B001'
const MIL = 'MI20-20260812-0105-B001'
const B12 = 'TB12-20260813-0106-B001'
const ONE12 = 'TO12-20260810-0103-B001'

const LOOSE: Loose[] = [
  ...[1, 2, 3].map(n => ({
    key: `slop-b20-old-${n}`,
    unit: 'Slop' as const,
    sku: 'TBC-BOLD-20',
    batch: B20_OLD,
    place: 'CT-A',
    at: n,
    retur: n === 1
  })),
  ...[1, 2, 3, 4, 5].map(n => ({
    key: `slop-b20-new-${n}`,
    unit: 'Slop' as const,
    sku: 'TBC-BOLD-20',
    batch: B20_NEW,
    place: 'CT-A',
    at: 10 + n
  })),
  ...[1, 2, 3, 4, 5].map(n => ({
    key: `slop-mil-${n}`,
    unit: 'Slop' as const,
    sku: 'MILLIONS',
    batch: MIL,
    place: 'CT-B',
    at: 20 + n
  })),
  ...[1, 2].map(n => ({
    key: `bal-b20a-${n}`,
    unit: 'Bal' as const,
    sku: 'TBC-BOLD-20',
    batch: B20_OLD,
    place: 'A3-P1',
    at: 30 + n
  })),
  ...[1, 2].map(n => ({
    key: `bal-b20b-${n}`,
    unit: 'Bal' as const,
    sku: 'TBC-BOLD-20',
    batch: B20_NEW,
    place: 'A3-P2',
    at: 40 + n,
    retur: n === 2
  })),
  ...[1, 2, 3].map(n => ({
    key: `bal-b12a-${n}`,
    unit: 'Bal' as const,
    sku: 'TBC-BOLD-12',
    batch: B12,
    place: 'A3-P3',
    at: 50 + n
  })),
  ...[1, 2].map(n => ({
    key: `bal-b12b-${n}`,
    unit: 'Bal' as const,
    sku: 'TBC-BOLD-12',
    batch: B12,
    place: 'A3-P4',
    at: 60 + n
  })),
  ...[1, 2].map(n => ({
    key: `slop-one-${n}`,
    unit: 'Slop' as const,
    sku: 'TBC-ONE-12',
    batch: ONE12,
    place: 'CT-A',
    at: 70 + n
  }))
]

const looseId = (key: string) => seedId(SEED_GROUP.fgLabel, 3000 + LOOSE.findIndex(item => item.key === key) + 1)

/** [kode, SKU, batch, qty, asal]. Pack sisa di CT-C — satu QR (PK-…) per baris. */
const PACKS: [string, string, string, number, string][] = [
  ['PK-BR20-0101', 'BRIGHTON-RED', 'BR20-20260805-0101-B001', 7, 'Produksi'],
  ['PK-TX20-0114', 'TBC-XTRA-20', 'TX20-20260818-0114-B001', 4, 'Produksi'],
  ['PK-TB20-0109', 'TBC-BOLD-20', B20_OLD, 4, 'Produksi'],
  ['PK-TB20-0111', 'TBC-BOLD-20', B20_NEW, 6, 'Produksi'],
  ['PK-MI20-0105', 'MILLIONS', MIL, 0, 'Produksi'],
  /** Pack Repack hasil claim (v7.50 A-03): tag ungu, hanya keluar lewat Permintaan Gabungan. */
  ['PK-TX20-RPK1', 'TBC-XTRA-20', 'TX20-20260818-0114-B001', 3, 'REPACK']
]
const packId = (code: string) => seedId(SEED_GROUP.fgPackStock, PACKS.findIndex(([c]) => c === code) + 1)

/* ------------------------------------------------------------ permintaan GAB */

type ItemRef = { label?: string; pack?: string; qty?: number; status: string }
type RequestSpec = {
  no: string
  kind: 'ALM' | 'PACK'
  jenis: 'Slop' | 'Bal' | 'Pack'
  sku: string
  alm?: 1 | 2
  source: string
  createdAt: string
  status: GabStatusCode
  operator?: 'budi' | 'sari'
  destination: string
  link?: string
  cancelReason?: string
  /** Menit sebelum waktu seed dibangun — permintaan menunggu yang masih di dalam jendela auto-approve (hitung mundur hidup). */
  agoMin?: number
  /** Disetujui otomatis oleh sistem (`ACT_GAB_AUTO_APPROVE`, tanpa aktor manusia). */
  auto?: boolean
  /** Alasan Tolak Admin (status Ditolak). */
  rejectReason?: string
  items: ItemRef[]
}

const keysOf = (prefix: string, n: number): ItemRef[] =>
  Array.from({ length: n }, (_, i) => ({ label: `${prefix}-${i + 1}`, status: 'RESERVED' }))

const REQUESTS: RequestSpec[] = [
  {
    no: 'GAB-260818-001',
    kind: 'ALM',
    jenis: 'Slop',
    sku: 'TBC-BOLD-20',
    alm: 1,
    source: 'ALM 1',
    createdAt: '2026-08-18 14:05',
    agoMin: 1,
    status: 'GAB_PENDING',
    destination: ALM_AREA[1],
    items: [...keysOf('slop-b20-old', 3), ...keysOf('slop-b20-new', 5)]
  },
  {
    no: 'GAB-260818-002',
    kind: 'ALM',
    jenis: 'Bal',
    sku: 'TBC-BOLD-20',
    alm: 2,
    source: 'ALM 2',
    createdAt: '2026-08-18 15:10',
    agoMin: 2,
    status: 'GAB_PENDING',
    destination: ALM_AREA[2],
    items: [...keysOf('bal-b20a', 2), ...keysOf('bal-b20b', 2)]
  },
  {
    no: 'GAB-260818-003',
    kind: 'ALM',
    jenis: 'Slop',
    sku: 'MILLIONS',
    alm: 1,
    source: 'ALM 1',
    createdAt: '2026-08-18 14:30',
    status: 'GAB_READY',
    auto: true,
    destination: ALM_AREA[1],
    items: keysOf('slop-mil', 5)
  },
  {
    no: 'GAB-260818-004',
    kind: 'PACK',
    jenis: 'Pack',
    sku: 'TBC-BOLD-20',
    source: 'Manual',
    createdAt: '2026-08-18 14:40',
    status: 'GAB_READY',
    operator: 'budi',
    destination: 'AREA-MSN-1',
    items: [
      { pack: 'PK-TB20-0109', qty: 4, status: 'RESERVED' },
      { pack: 'PK-TB20-0111', qty: 6, status: 'RESERVED' }
    ]
  },
  {
    no: 'GAB-260817-005',
    kind: 'ALM',
    jenis: 'Slop',
    sku: 'TBC-ONE-12',
    alm: 1,
    source: 'ALM 1',
    createdAt: '2026-08-17 10:20',
    status: 'GAB_DONE',
    operator: 'sari',
    destination: ALM_AREA[1],
    items: keysOf('slop-one', 2).map(item => ({ ...item, status: 'AT_ALM' }))
  },
  {
    no: 'GAB-260817-006',
    kind: 'PACK',
    jenis: 'Pack',
    sku: 'MILLIONS',
    source: 'Manual',
    createdAt: '2026-08-17 13:00',
    status: 'GAB_DONE',
    operator: 'budi',
    destination: 'AREA-STG-PRD-1',
    items: [{ pack: 'PK-MI20-0105', qty: 5, status: 'HANDED_OVER' }]
  },
  {
    no: 'GAB-260816-007',
    kind: 'PACK',
    jenis: 'Pack',
    sku: 'BRIGHTON-RED',
    source: 'Manual',
    createdAt: '2026-08-16 15:30',
    status: 'GAB_CANCELLED',
    operator: 'sari',
    destination: 'AREA-MSN-1',
    cancelReason: 'Produksi tidak jadi memakai pack',
    items: [{ pack: 'PK-BR20-0101', qty: 7, status: 'RELEASED' }]
  },
  {
    no: 'GAB-260817-008',
    kind: 'ALM',
    jenis: 'Slop',
    sku: 'MILLIONS',
    alm: 1,
    source: 'ALM 1',
    createdAt: '2026-08-17 15:45',
    status: 'GAB_REJECTED',
    destination: ALM_AREA[1],
    rejectReason: 'Sisa Slop Millions sudah dihitung ulang bersama SPV, tidak perlu ikut ALM',
    items: keysOf('slop-mil', 5).map(item => ({ ...item, status: 'RELEASED' }))
  }
]

const GAB_PATH: Record<GabStatusCode, GabActionName[]> = {
  GAB_PENDING: [],
  GAB_READY: ['ACT_GAB_APPROVE'],
  GAB_PICKING: ['ACT_GAB_APPROVE', 'ACT_GAB_START_PICK'],
  GAB_DONE: ['ACT_GAB_APPROVE', 'ACT_GAB_START_PICK', 'ACT_GAB_COMPLETE'],
  GAB_CANCELLED: ['ACT_GAB_APPROVE', 'ACT_GAB_CANCEL_READY'],
  GAB_REJECTED: ['ACT_GAB_REJECT']
}
const GAB_ACTOR: Record<string, 'ACT_GAB_ADMIN_FG' | 'ACT_GAB_OP_FG'> = {
  ACT_GAB_REJECT: 'ACT_GAB_ADMIN_FG',
  ACT_GAB_APPROVE: 'ACT_GAB_ADMIN_FG',
  ACT_GAB_START_PICK: 'ACT_GAB_OP_FG',
  ACT_GAB_COMPLETE: 'ACT_GAB_OP_FG',
  ACT_GAB_CANCEL_READY: 'ACT_GAB_ADMIN_FG'
}

/* ------------------------------------------------------------ tugas KSL */

const KSL_PATH: Record<KslStatusCode, KslActionName[]> = {
  KSL_ASSIGNED: [],
  KSL_LOADED: ['ACT_KSL_CARRY'],
  KSL_DONE: ['ACT_KSL_CARRY', 'ACT_KSL_COMPLETE'],
  KSL_STOPPED: ['ACT_KSL_STOP_ASSIGNED']
}
const KSL_USER: Record<string, string> = {
  ACT_KSL_CARRY: 'op',
  ACT_KSL_COMPLETE: 'op',
  ACT_KSL_STOP_ASSIGNED: 'admin',
  ACT_KSL_STOP_LOADED: 'admin'
}
const KSL_ACTOR: Record<string, 'ACT_KSL_OP_FG' | 'ACT_KSL_ADMIN_FG'> = {
  ACT_KSL_CARRY: 'ACT_KSL_OP_FG',
  ACT_KSL_COMPLETE: 'ACT_KSL_OP_FG',
  ACT_KSL_STOP_ASSIGNED: 'ACT_KSL_ADMIN_FG',
  ACT_KSL_STOP_LOADED: 'ACT_KSL_ADMIN_FG'
}

type TaskSpec = {
  no: string
  sku: string
  jenis: 'Karton'
  from: string
  to: string
  take: number
  fill: 18 | 24
  reason?: string
  status: KslStatusCode
  operator: 'budi' | 'sari'
  createdAt: string
  itemStatus: 'WAITING' | 'MOVED' | 'RELEASED'
  stopReason?: string
  /** Label diambil dari pallet ini (bila beda dengan `from`, mis. riwayat yang sudah pindah). */
  labelsFrom?: string
}

const TASKS: TaskSpec[] = [
  {
    no: 'KSL-260818-001',
    sku: 'BRIGHTON-RED',
    jenis: 'Karton',
    from: 'D1-P2',
    to: 'D1-P1',
    take: 4,
    fill: 24,
    reason: 'Tidak ada pallet kosong tersisa, karton masuk menunggu di staging',
    status: 'KSL_ASSIGNED',
    operator: 'budi',
    createdAt: '2026-08-18 13:40',
    itemStatus: 'WAITING'
  },
  {
    no: 'KSL-260817-001',
    sku: 'TBC-XTRA-20',
    jenis: 'Karton',
    from: 'B3-P2',
    to: 'B3-P1',
    take: 2,
    fill: 18,
    status: 'KSL_DONE',
    operator: 'sari',
    createdAt: '2026-08-17 09:10',
    itemStatus: 'MOVED',
    labelsFrom: 'B3-P1'
  },
  {
    no: 'KSL-260817-002',
    sku: 'ARUM-MANIS',
    jenis: 'Karton',
    from: 'D3-P1',
    to: 'D2-P4',
    take: 3,
    fill: 24,
    reason: 'Gudang penuh, pallet D3-P1 diperlukan untuk penerimaan',
    status: 'KSL_STOPPED',
    operator: 'budi',
    createdAt: '2026-08-17 11:00',
    itemStatus: 'RELEASED',
    stopReason: 'Operator dialihkan ke picking mendesak'
  }
]

/* ------------------------------------------------------------ pembangun */

export function applyMergeData(seed: SeedTables): SeedTables {
  const fg_label: LocalRow[] = [...(seed.fg_label ?? [])]
  const labelStatus = new Map<string, string>()
  const reservedPacks = new Set<string>()
  const wxl_submission: LocalRow[] = []
  const wxl_submission_log: LocalRow[] = []
  const merge_request: LocalRow[] = []
  const merge_request_item: LocalRow[] = []
  const consolidation_task: LocalRow[] = []
  const consolidation_task_item: LocalRow[] = []
  let logNo = 0
  let itemNo = 0

  /* label barang lepas */
  LOOSE.forEach(item => {
    const head = labelHead(item.batch)
    const letter = item.unit === 'Slop' ? 'S' : 'B'
    const isPallet = !item.place.startsWith('CT-')
    const done = item.key.startsWith('slop-one')
    fg_label.push({
      ...audit(ulil, '2026-08-17 08:00'),
      id: looseId(item.key),
      label_no: `JLC:${letter}:F:${head.short}:${head.seq}:${head.date}:${clock(7, 0, item.at)}:B001:${String(5000 + item.at).padStart(4, '0')}`,
      sku_id: fgSkuId(item.sku),
      batch_id: batchId(item.batch),
      level: item.unit,
      gtin: gtinOf(item.sku),
      parent_label_id: null,
      zone_id: isPallet ? zoneId(item.place.split('-')[0]) : null,
      pallet_id: isPallet ? palletId(item.place) : null,
      container_id: isPallet ? null : containerId(item.place),
      label_status: done ? 'MERGED' : 'IN_STOCK',
      is_priority_lot: Boolean(item.retur)
    })
  })

  /* permintaan GAB */
  REQUESTS.forEach((spec, index) => {
    const requestId = seedId(SEED_GROUP.mergeRequest, index + 1)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 1100 + index + 1)
    const operatorId = spec.operator ? USER_IDS[spec.operator] : null
    /* Permintaan auto-approve: aksi pertama diganti aksi sistem (tanpa aktor); permintaan menunggu dibuat relatif ke waktu seed. */
    const path = GAB_PATH[spec.status].map(action =>
      spec.auto && action === 'ACT_GAB_APPROVE' ? ('ACT_GAB_AUTO_APPROVE' as GabActionName) : action
    )
    const createdAt =
      spec.agoMin !== undefined ? new Date(Date.now() - spec.agoMin * 60_000).toISOString() : spec.createdAt
    const approved = path.includes('ACT_GAB_APPROVE') || path.includes('ACT_GAB_AUTO_APPROVE')
    merge_request.push({
      ...audit(ulil, createdAt),
      id: requestId,
      png_no: spec.no,
      operator_id: operatorId,
      merge_status: spec.status.replace('GAB_', ''),
      status_id: WF_GAB.statusId(spec.status),
      submission_id: submissionId,
      kind: spec.kind,
      jenis: spec.jenis,
      sku_id: fgSkuId(spec.sku),
      alm_no: spec.alm ?? null,
      source_text: spec.source,
      destination_code: spec.destination,
      linked_request_id: null,
      approved_by_id: approved && !spec.auto ? ulil : null,
      approved_at: approved ? utc(createdAt) : null,
      completed_at: spec.status === 'GAB_DONE' ? utc(spec.createdAt) : null,
      cancel_reason: spec.cancelReason ?? spec.rejectReason ?? null
    })
    wxl_submission.push({
      ...audit(ulil, createdAt),
      id: submissionId,
      workflow_id: WF_GAB.workflowId,
      entity: 'merge_request',
      entity_id: requestId,
      source: 'ACTION',
      current_status_id: WF_GAB.statusId(spec.status),
      last_action_id: path.length ? WF_GAB.actionId(path[path.length - 1]) : null,
      workflow_snapshot: null
    })
    path.forEach(action => {
      const [from, to] = WF_GAB.actionStatus[action]
      logNo += 1
      const system = action === 'ACT_GAB_AUTO_APPROVE'
      wxl_submission_log.push({
        ...audit(
          system ? null : GAB_ACTOR[action] === 'ACT_GAB_OP_FG' && operatorId ? operatorId : ulil,
          spec.createdAt
        ),
        id: seedId(SEED_GROUP.wxlSubmissionLog, 20000 + logNo),
        workflow_id: WF_GAB.workflowId,
        submission_id: submissionId,
        action_id: WF_GAB.actionId(action),
        actor_id: system ? null : WF_GAB.actorId(GAB_ACTOR[action]),
        from_status_id: WF_GAB.statusId(from),
        to_status_id: WF_GAB.statusId(to),
        approval_total_count: 1,
        approval_approved_count: 1,
        approval_rejected_count: 0,
        reason:
          action === 'ACT_GAB_CANCEL_READY'
            ? (spec.cancelReason ?? null)
            : action === 'ACT_GAB_REJECT'
              ? (spec.rejectReason ?? null)
              : system
                ? 'Disetujui otomatis oleh sistem'
                : null,
        entity: 'merge_request',
        entity_id: requestId
      })
    })
    spec.items.forEach(ref => {
      itemNo += 1
      const loose = ref.label ? LOOSE.find(item => item.key === ref.label) : undefined
      const pack = ref.pack ? PACKS.find(([code]) => code === ref.pack) : undefined
      merge_request_item.push({
        ...audit(ulil, spec.createdAt),
        id: seedId(SEED_GROUP.mergeRequestItem, itemNo),
        request_id: requestId,
        label_id: loose ? looseId(loose.key) : null,
        pack_stock_id: pack ? packId(pack[0]) : null,
        sku_id: fgSkuId(spec.sku),
        batch_id: batchId(loose?.batch ?? pack?.[2] ?? ''),
        unit_type: spec.jenis,
        qty_plan: loose ? 1 : (ref.qty ?? 0),
        qty_taken: ref.status === 'HANDED_OVER' ? (ref.qty ?? 0) : null,
        from_pallet_id: loose && !loose.place.startsWith('CT-') ? palletId(loose.place) : null,
        from_container_id: loose?.place.startsWith('CT-')
          ? containerId(loose.place)
          : pack
            ? containerId('CT-C')
            : null,
        is_return: Boolean(loose?.retur),
        origin: loose?.retur ? 'Retur' : (pack?.[4] ?? 'Produksi'),
        item_status: ref.status
      })
      if (loose && ref.status === 'RESERVED') labelStatus.set(looseId(loose.key), 'MERGE_RESERVED')
      if (pack && ref.status === 'RESERVED') reservedPacks.add(pack[0])
    })
  })

  /* tugas KSL contoh */
  const kartonOn = (pallet: string) =>
    fg_label.filter(
      row => row.level === 'Karton' && row.pallet_id === palletId(pallet) && row.label_status === 'IN_STOCK'
    )
  TASKS.forEach((spec, index) => {
    const taskId = seedId(SEED_GROUP.consolidationTask, index + 1)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 1200 + index + 1)
    const operatorId = USER_IDS[spec.operator]
    const path = KSL_PATH[spec.status]
    consolidation_task.push({
      ...audit(ulil, spec.createdAt),
      id: taskId,
      ksl_no: spec.no,
      sku_id: fgSkuId(spec.sku),
      from_pallet_id: palletId(spec.from),
      to_pallet_id: palletId(spec.to),
      target_fill: spec.fill,
      fill_reason: spec.reason ?? null,
      task_status: spec.status.replace('KSL_', ''),
      status_id: WF_KSL.statusId(spec.status),
      submission_id: submissionId,
      operator_id: operatorId,
      jenis: spec.jenis,
      package_no: null,
      freed_count: 1,
      stop_reason: spec.stopReason ?? null
    })
    wxl_submission.push({
      ...audit(ulil, spec.createdAt),
      id: submissionId,
      workflow_id: WF_KSL.workflowId,
      entity: 'consolidation_task',
      entity_id: taskId,
      source: 'ACTION',
      current_status_id: WF_KSL.statusId(spec.status),
      last_action_id: path.length ? WF_KSL.actionId(path[path.length - 1]) : null,
      workflow_snapshot: null
    })
    path.forEach(action => {
      const [from, to] = WF_KSL.actionStatus[action]
      logNo += 1
      wxl_submission_log.push({
        ...audit(KSL_USER[action] === 'op' ? operatorId : ulil, spec.createdAt),
        id: seedId(SEED_GROUP.wxlSubmissionLog, 30000 + logNo),
        workflow_id: WF_KSL.workflowId,
        submission_id: submissionId,
        action_id: WF_KSL.actionId(action),
        actor_id: WF_KSL.actorId(KSL_ACTOR[action]),
        from_status_id: WF_KSL.statusId(from),
        to_status_id: WF_KSL.statusId(to),
        approval_total_count: 1,
        approval_approved_count: 1,
        approval_rejected_count: 0,
        reason: action === 'ACT_KSL_STOP_ASSIGNED' ? (spec.stopReason ?? null) : null,
        entity: 'consolidation_task',
        entity_id: taskId
      })
    })
    kartonOn(spec.labelsFrom ?? spec.from)
      .slice(0, spec.take)
      .forEach((label, n) => {
        consolidation_task_item.push({
          ...audit(ulil, spec.createdAt),
          id: seedId(SEED_GROUP.consolidationTaskItem, index * 20 + n + 1),
          task_id: taskId,
          label_id: label.id,
          unit_type: spec.jenis,
          from_pallet_id: palletId(spec.from),
          to_pallet_id: palletId(spec.to),
          final_pallet_id: spec.itemStatus === 'MOVED' ? palletId(spec.to) : null,
          item_status: spec.itemStatus,
          manual_reason: null
        })
        if (spec.itemStatus === 'WAITING') labelStatus.set(String(label.id), 'CONSOLIDATION_RESERVED')
      })
  })

  const labels = fg_label.map(row =>
    labelStatus.has(String(row.id)) ? { ...row, label_status: labelStatus.get(String(row.id)) } : row
  )
  return {
    ...seed,
    warehouse_zone: [
      ...(seed.warehouse_zone ?? []),
      ...MERGE_AREAS.map(([code, name, type]) => ({
        ...audit(ulil, '2026-08-01 07:00'),
        id: mergeAreaId(code),
        code,
        name,
        warehouse_id: FG_WAREHOUSE_ID,
        zone_type: type
      }))
    ],
    fg_label: labels,
    fg_pack_stock: PACKS.map(([code, sku, batch, qty, origin]) => ({
      ...audit(ulil, '2026-08-17 08:00'),
      id: packId(code),
      sku_id: fgSkuId(sku),
      batch_id: batchId(batch),
      container_id: containerId('CT-C'),
      qty_pack: qty,
      label_no: code,
      origin,
      status: qty <= 0 ? 'GONE' : reservedPacks.has(code) ? 'RESERVED' : 'IN_STOCK'
    })),
    merge_request,
    merge_request_item,
    consolidation_task,
    consolidation_task_item,
    wxl_submission: [...(seed.wxl_submission ?? []), ...wxl_submission],
    wxl_submission_log: [...(seed.wxl_submission_log ?? []), ...wxl_submission_log]
  }
}
